/**
 * Dialog showing cancelled and no-show appointments
 * Displays up to 3 upcoming appointments per cancelled event
 * Sorted by nearest upcoming appointment date
 */

import { useState, useCallback, useRef, useEffect, Fragment } from 'react'
import { ArrowRight, CalendarX, UserX } from 'lucide-react'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { STATUS_BACKGROUNDS } from '@/lib/constants'
import { trpc } from '@/trpc/client'
import type { CancelledEventRow } from '@/types/calendar'

interface CancelledEventsDialogProps {
	open: boolean
	onClose: () => void
	initialTab?: 'cancelled' | 'noshow'
}

export function CancelledEventsDialog({
	open,
	onClose,
	initialTab = 'cancelled',
}: CancelledEventsDialogProps) {
	const [activeTab, setActiveTab] = useState<'cancelled' | 'noshow'>(initialTab)
	const [page, setPage] = useState(0)
	const [allEvents, setAllEvents] = useState<CancelledEventRow[]>([])
	const [hasMore, setHasMore] = useState(false)
	const contentRef = useRef<HTMLDivElement>(null)

	// Reset when dialog opens or tab changes
	useEffect(() => {
		if (open) {
			setPage(0)
			setAllEvents([])
			setHasMore(false)
			setActiveTab(initialTab)
		}
	}, [open, initialTab])

	// Reset pagination when tab changes
	useEffect(() => {
		setPage(0)
		setAllEvents([])
		setHasMore(false)
	}, [activeTab])

	// Fetch cancelled events with pagination (searches across both ZUB and DH)
	const { data: cancelledData, isLoading: isCancelledLoading, isFetching: isCancelledFetching } = trpc.calendar.settings.cancelled.useQuery(
		{
			page,
			limit: 20,
		},
		{
			enabled: open && activeTab === 'cancelled',
			staleTime: 30000,
		},
	)

	// Fetch no-show events with pagination (searches across both ZUB and DH)
	const { data: noshowData, isLoading: isNoshowLoading, isFetching: isNoshowFetching } = trpc.calendar.settings.noshow.useQuery(
		{
			page,
			limit: 20,
		},
		{
			enabled: open && activeTab === 'noshow',
			staleTime: 30000,
		},
	)

	// Use the appropriate data based on active tab
	const data = activeTab === 'cancelled' ? cancelledData : noshowData
	const isLoading = activeTab === 'cancelled' ? isCancelledLoading : isNoshowLoading
	const isFetching = activeTab === 'cancelled' ? isCancelledFetching : isNoshowFetching

	// Accumulate events when new data arrives
	useEffect(() => {
		if (data?.items) {
			if (page === 0) {
				setAllEvents(data.items)
			} else {
				setAllEvents((prev) => [...prev, ...data.items])
			}
			setHasMore(data.hasMore)
		}
	}, [data, page])

	const handleLoadMore = useCallback(() => {
		if (!isFetching && hasMore) {
			setPage((p) => p + 1)
		}
	}, [isFetching, hasMore])

	// Handle scroll for infinite loading
	const handleScroll = useCallback(() => {
		if (!contentRef.current || isFetching || !hasMore) return

		const { scrollTop, scrollHeight, clientHeight } = contentRef.current
		if (scrollTop + clientHeight >= scrollHeight - 50) {
			handleLoadMore()
		}
	}, [handleLoadMore, hasMore, isFetching])

	// Events are now fetched from separate endpoints, no client-side filtering needed
	const isLoadingMore = isFetching && page > 0

	return (
		<Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
			<DialogContent className="max-h-[90vh] w-[70vw] max-w-[70vw] overflow-hidden">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-3">
						<span className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#f5ebe0]">
							<CalendarX className="w-5 h-5 text-[#d7ad6c]" />
						</span>
						<span>Přehled zrušených termínů</span>
					</DialogTitle>
				</DialogHeader>

				<Tabs
					value={activeTab}
					onValueChange={(v) => setActiveTab(v as 'cancelled' | 'noshow')}
					className="mt-2 w-full overflow-hidden"
				>
					<TabsList className="grid w-full grid-cols-2 bg-[#f5ebe0] p-1 rounded-xl">
						<TabsTrigger
							value="cancelled"
							className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#3d3428] data-[state=active]:shadow-sm text-[#8c8275]"
						>
							Zrušené
						</TabsTrigger>
						<TabsTrigger
							value="noshow"
							className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#3d3428] data-[state=active]:shadow-sm text-[#8c8275]"
						>
							Nepřišli
						</TabsTrigger>
					</TabsList>

					<TabsContent value="cancelled" className="mt-6 w-full overflow-hidden">
						<div
							ref={contentRef}
							onScroll={handleScroll}
							className="max-h-[60vh] space-y-4 overflow-y-auto overflow-x-hidden w-full pr-2"
							style={{ scrollbarWidth: 'thin', scrollbarColor: '#d7ad6c #f5ebe0' }}
						>
							{isLoading && page === 0 ? (
								<LoadingState />
							) : allEvents.length === 0 ? (
								<EmptyState message="Žádné zrušené termíny" />
							) : (
								<>
									{allEvents.map((row: CancelledEventRow, index: number) => (
										<CancelledRow key={`${row.cancelled.id}-${index}`} row={row} type="cancelled" />
									))}
									{isLoadingMore && <LoadingMore />}
								</>
							)}
						</div>
					</TabsContent>

					<TabsContent value="noshow" className="mt-6 w-full overflow-hidden">
						<div
							ref={contentRef}
							onScroll={handleScroll}
							className="max-h-[60vh] space-y-4 overflow-y-auto overflow-x-hidden w-full pr-2"
							style={{ scrollbarWidth: 'thin', scrollbarColor: '#d7ad6c #f5ebe0' }}
						>
							{isLoading && page === 0 ? (
								<LoadingState />
							) : allEvents.length === 0 ? (
								<EmptyState message="Žádní pacienti, kteří nepřišli" />
							) : (
								<>
									{allEvents.map((row: CancelledEventRow, index: number) => (
										<CancelledRow key={`${row.cancelled.id}-${index}`} row={row} type="noshow" />
									))}
									{isLoadingMore && <LoadingMore />}
								</>
							)}
						</div>
					</TabsContent>
				</Tabs>
			</DialogContent>
		</Dialog>
	)
}

// Helper components
function LoadingState() {
	return (
		<div className="flex items-center justify-center py-12">
			<div className="h-6 w-6 animate-spin rounded-full border-2 border-[#d7ad6c] border-t-transparent" />
			<span className="ml-3 text-[#8c8275]">Načítání...</span>
		</div>
	)
}

function LoadingMore() {
	return (
		<div className="flex items-center justify-center py-4">
			<div className="h-4 w-4 animate-spin rounded-full border-2 border-[#d7ad6c] border-t-transparent" />
			<span className="ml-2 text-sm text-[#8c8275]">
				Načítání dalších...
			</span>
		</div>
	)
}

function EmptyState({ message }: { message: string }) {
	return (
		<div className="flex flex-col items-center justify-center py-12 text-center">
			<div className="w-16 h-16 rounded-2xl bg-[#f5ebe0] flex items-center justify-center mb-4">
				<UserX className="w-8 h-8 text-[#d7ad6c]" />
			</div>
			<p className="text-[#8c8275]">{message}</p>
		</div>
	)
}

interface CancelledRowProps {
	row: CancelledEventRow
	type: 'cancelled' | 'noshow'
}

// Format date as "dd. mm. yyyy"
function formatDateDisplay(dateStr: string) {
	const date = new Date(dateStr)
	const day = String(date.getDate()).padStart(2, '0')
	const month = String(date.getMonth() + 1).padStart(2, '0')
	const year = date.getFullYear()
	return `${day}. ${month}. ${year}`
}

// Format type display (uppercase)
function formatTypeDisplay(appointmentType: string) {
	return appointmentType === 'ZUB' ? 'ZUB' : 'DH'
}

// Arrow separator component
function ArrowSeparator() {
	return (
		<div className="flex items-center flex-shrink-0 px-1">
			<div className="w-6 h-6 rounded-full bg-[#f5ebe0] flex items-center justify-center">
				<ArrowRight className="h-3 w-3 text-[#d7ad6c]" />
			</div>
		</div>
	)
}

// Event card component
interface EventCardProps {
	title: string | null
	date: string
	startTime: string
	endTime: string
	appointmentType: string
	background: string
	patients: { id: string; personalId: string; firstName: string; lastName: string }[]
}

function EventCard({ title, date, startTime, endTime, appointmentType, background, patients }: EventCardProps) {
	return (
		<div
			className="flex-1 min-w-0 shrink rounded-xl p-3 text-white overflow-hidden"
			style={{ backgroundColor: background }}
		>
			<h4 className="mb-2 font-medium truncate text-base">{title || 'Bez názvu'}</h4>
			<p className="text-base opacity-90 truncate">
				Datum: {formatDateDisplay(date)}, {startTime} - {endTime}
			</p>
			<p className="text-base opacity-90">
				Typ ošetření: {formatTypeDisplay(appointmentType)}
			</p>
			{patients.length > 0 && (
				<div className="mt-2 pt-2 border-t border-white/20">
					<p className="text-sm opacity-75 mb-1">Pacienti:</p>
					<ul className="text-base space-y-0.5">
						{patients.map((p) => (
							<li key={p.id} className="truncate">
								<a
									href={`/patient/${encodeURIComponent(p.personalId)}`}
									target="_blank"
									rel="noopener noreferrer"
									className="underline hover:no-underline"
								>
									{p.lastName} {p.firstName} - {p.personalId}
								</a>
							</li>
						))}
					</ul>
				</div>
			)}
		</div>
	)
}

function CancelledRow({ row, type }: CancelledRowProps) {
	const { cancelled, upcoming } = row

	return (
		<div className="flex items-stretch gap-2 rounded-2xl border border-[#e8e4df] bg-white p-3 shadow-sm w-full overflow-hidden">
			{/* Cancelled event */}
			<EventCard
				title={cancelled.title}
				date={cancelled.date}
				startTime={cancelled.startTime}
				endTime={cancelled.endTime}
				appointmentType={cancelled.type}
				background={type === 'noshow' ? STATUS_BACKGROUNDS.NO_SHOW : STATUS_BACKGROUNDS.CANCELLED}
				patients={cancelled.patients}
			/>

			{/* Arrow after cancelled */}
			<ArrowSeparator />

			{/* Upcoming events with arrows between them, or empty state */}
			{upcoming.length > 0 ? (
				upcoming.map((upcomingEvent, index) => (
					<Fragment key={upcomingEvent.id}>
						{index > 0 && <ArrowSeparator />}
						<EventCard
							title={upcomingEvent.title}
							date={upcomingEvent.date}
							startTime={upcomingEvent.startTime}
							endTime={upcomingEvent.endTime}
							appointmentType={upcomingEvent.type}
							background={upcomingEvent.background || '#f4511e'}
							patients={upcomingEvent.patients}
						/>
					</Fragment>
				))
			) : (
				<div className="flex flex-1 min-w-0 shrink items-center justify-center rounded-xl border-2 border-dashed border-[#e8e4df] bg-[#faf8f5] p-3">
					<p className="text-center text-xs text-[#8c8275]">
						Neexistuje nadcházející termín
					</p>
				</div>
			)}
		</div>
	)
}
